import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";

const generateValueFromDisplayText = (text) => text
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

const EMPTY_VALUES = [];

function AddDropdownModal({
  show,
  onClose,
  onSubmit,
  isSubmitting = false,
  mode = "create",
  initialDropdownType = "",
  initialValues = EMPTY_VALUES,
}) {
  const [dropdownType, setDropdownType] = useState("");
  const [textAreaValue, setTextAreaValue] = useState("");
  const [validationError, setValidationError] = useState("");
  const isEditMode = mode === "edit";

  useEffect(() => {
    if (!show) return;
    setDropdownType(initialDropdownType);
    setTextAreaValue(initialValues.join("\n"));
    setValidationError("");
  }, [initialDropdownType, initialValues, show]);

  const values = useMemo(() => (
    textAreaValue
      .split(/\r?\n/)
      .map((row) => row.trim())
      .filter(Boolean)
  ), [textAreaValue]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedType = dropdownType.trim();

    if (!normalizedType) {
      setValidationError("Enter a dropdown name.");
      return;
    }

    if (values.length === 0) {
      setValidationError("Add at least one value.");
      return;
    }

    const uniqueValues = new Set(values.map((value) => value.toLocaleLowerCase()));
    if (uniqueValues.size !== values.length) {
      setValidationError("Each display label must be unique.");
      return;
    }

    const normalizedValues = values.map((displayText, index) => ({
      dropdownType: normalizedType,
      displayText,
      value: generateValueFromDisplayText(displayText),
      sortOrder: index + 1,
      isActive: true,
    }));

    setValidationError("");
    try {
      await onSubmit(normalizedValues);
    } catch (_) {
      // The parent presents the API error and keeps this modal open for recovery.
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!isSubmitting}>
          <div>
            <p className="dropdown-admin__eyebrow mb-1">{isEditMode ? "Bulk edit" : "Create"}</p>
            <Modal.Title>{isEditMode ? `Edit ${initialDropdownType}` : "New dropdown"}</Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body>
          <p className="text-muted">
            {isEditMode
              ? "Edit one display label per line. Line order determines the dropdown sort order."
              : "Name the dropdown, then paste one display label per line. Stored values and ordering are generated automatically."}
          </p>

          {isEditMode && (
            <Alert variant="warning">
              Removing a line permanently deletes that dropdown value. Existing active and inactive states are preserved for values that remain.
            </Alert>
          )}

          {validationError && <Alert variant="danger">{validationError}</Alert>}

          <Form.Group className="mb-4" controlId="new-dropdown-type">
            <Form.Label>Dropdown name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Example: Assembly Project Status"
              value={dropdownType}
              onChange={(event) => setDropdownType(event.target.value)}
              disabled={isSubmitting || isEditMode}
              autoFocus={!isEditMode}
            />
            <Form.Text>
              {isEditMode
                ? "The dropdown name cannot be changed in the bulk editor."
                : "Use a descriptive name that administrators will recognize."}
            </Form.Text>
          </Form.Group>

          <Form.Group controlId="new-dropdown-values">
            <div className="d-flex justify-content-between align-items-baseline gap-3">
              <Form.Label>Display labels</Form.Label>
              <span className="text-muted small">
                {values.length} value{values.length === 1 ? "" : "s"}
              </span>
            </div>
            <Form.Control
              as="textarea"
              rows={8}
              placeholder={"Ready to Start\nIn Progress\nComplete"}
              value={textAreaValue}
              onChange={(event) => setTextAreaValue(event.target.value)}
              disabled={isSubmitting}
              autoFocus={isEditMode}
            />
            <Form.Text>
              {isEditMode
                ? "Reorder lines to change Sort Order. You can also paste a single column from Excel."
                : "You can paste a single column directly from Excel."}
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting || !dropdownType.trim() || values.length === 0}>
            {isSubmitting && <Spinner size="sm" className="me-2" />}
            {isEditMode ? "Save all values" : "Create dropdown"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default AddDropdownModal;
